import * as React from "react";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import NavBar from "./NavBar";
import img1 from "../images/Group 2.svg";
import {
  BrandingWatermark,
  Category,
  Edit,
  EditAttributes,
  ExpandMore,
  Home,
  Inventory2,
  Logout,
  Pages,
  ShoppingBasket,
  Storefront,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  Grid,
} from "@mui/material";
import DashBoardScreen from "../screens/DashBoardScreen";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { logout } from "../actions/userActions";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";

const drawerWidth = 240;

const useStyles = (theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
  },
});

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function SideBar() {
  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const { palette } = useTheme();
  const navigate = useNavigate();
  const user = null;
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Loggged out successfully");
    navigate("/", {
      replace: true,
    });
  };

  //check device width
  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth < 992;
  const isDesktop = window.innerWidth >= 992;
  const isLargeDesktop = window.innerWidth >= 1200;

  // if is desktop setOpen to true
  React.useEffect(() => {
    if (isDesktop) {
      setOpen(true);
    }
  }, [isDesktop]);

  const themeChange = useSelector((state) => state.themeChange);
  const { theme } = themeChange;

  return (
    <>
      {userInfo && (
        <>
          <AppBar position="fixed" open={open}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  marginRight: "36px",
                  ...(open && { display: "none" }),
                }}
              >
                <MenuIcon />
              </IconButton>
              <NavBar />
            </Toolbar>
          </AppBar>
          <Drawer
            PaperProps={{
              style: {
                backgroundColor:
                  theme === "light"
                    ? palette.primary.main
                    : palette.background.paper,
              },
            }}
            variant="permanent"
            open={open}
          >
            <DrawerHeader>
              <img src={img1} height={50} width={50} />{" "}
              <Typography
                sx={{
                  color:
                    theme === "light"
                      ? palette.primary.contrastText
                      : palette.text.secondary,
                }}
                variant="h6"
                mx={2}
              >
                Elegant
              </Typography>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "rtl" ? (
                  <ChevronRightIcon />
                ) : (
                  <ChevronLeftIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List>
              <ListItem
                sx={{
                  color:
                    theme === "light"
                      ? palette.primary.contrastText
                      : palette.text.secondary,
                }}
                button
                onClick={() => navigate("/dashboard")}
              >
                <ListItemIcon>
                  <Home
                    sx={{
                      color:
                        theme === "light"
                          ? palette.primary.contrastText
                          : palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItem>
              <ListItem
                sx={{
                  color:
                    theme === "light"
                      ? palette.primary.contrastText
                      : palette.text.secondary,
                }}
                button
                onClick={() => navigate("brands")}
              >
                <ListItemIcon>
                  <BrandingWatermark
                    sx={{
                      color:
                        theme === "light"
                          ? palette.primary.contrastText
                          : palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Brands" />
              </ListItem>
              <ListItem
                sx={{
                  color:
                    theme === "light"
                      ? palette.primary.contrastText
                      : palette.text.secondary,
                }}
                onClick={() => navigate("category")}
                button
              >
                <ListItemIcon>
                  <Category
                    sx={{
                      color:
                        theme === "light"
                          ? palette.primary.contrastText
                          : palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Categories" />
              </ListItem>

              <ListItem
                sx={{
                  color:
                    theme === "light"
                      ? palette.primary.contrastText
                      : palette.text.secondary,
                }}
                button
                onClick={() => navigate("attribute")}
              >
                <ListItemIcon>
                  <EditAttributes
                    sx={{
                      color:
                        theme === "light"
                          ? palette.primary.contrastText
                          : palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Attributes" />
              </ListItem>
            </List>
            <Divider />

            <List>
              <>
                <ListItem button onClick={() => navigate("store")}>
                  <>
                    <ListItemIcon>
                      <Storefront
                        sx={{
                          color:
                            theme === "light"
                              ? palette.primary.contrastText
                              : palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Stores"
                      sx={{
                        color:
                          theme === "light"
                            ? palette.primary.contrastText
                            : palette.text.secondary,
                      }}
                    />
                  </>
                </ListItem>
              </>
              <>
                <ListItem
                  button
                  sx={{
                    color:
                      theme === "light"
                        ? palette.primary.contrastText
                        : palette.text.secondary,
                  }}
                  onClick={() => navigate("products")}
                >
                  <>
                    <ListItemIcon>
                      <Inventory2
                        sx={{
                          color:
                            theme === "light"
                              ? palette.primary.contrastText
                              : palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Products" />
                  </>
                </ListItem>
              </>
              <>
                <ListItem
                  button
                  sx={{
                    color:
                      theme === "light"
                        ? palette.primary.contrastText
                        : palette.text.secondary,
                  }}
                  onClick={() => navigate("orders")}
                >
                  <>
                    <ListItemIcon>
                      <ShoppingBasket
                        sx={{
                          color:
                            theme === "light"
                              ? palette.primary.contrastText
                              : palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        color:
                          theme === "light"
                            ? palette.primary.contrastText
                            : palette.text.secondary,
                      }}
                      primary="Orders"
                    />
                  </>
                </ListItem>
                <ListItem
                  button
                  sx={{
                    display: {
                      md: "none",
                      xs: "flex",
                    },
                  }}
                  onClick={handleLogout}
                >
                  <>
                    <ListItemIcon>
                      <Logout
                        sx={{
                          color:
                            theme === "light"
                              ? palette.primary.contrastText
                              : palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        color:
                          theme === "light"
                            ? palette.primary.contrastText
                            : palette.text.secondary,
                      }}
                      primary="Logout"
                    />
                  </>
                </ListItem>
              </>
            </List>
          </Drawer>
        </>
      )}
    </>
  );
}
